/**
 * Default, demoable {@link SessionsRepository} implementation.
 *
 * Seeds from the shared fixtures + a handful of "today / upcoming" sessions
 * (generated relative to `Date.now()` so the agenda is never empty) and maps
 * them through `mapCoreSessionsToDomain` — the canonical core→domain mapping.
 * Check-in / check-out are applied locally with the SAME field semantics as the
 * backend `update-session-state` Lambda and persisted to `localStorage` so the
 * flow survives reloads and is demoable end-to-end without any network.
 */

import {
  mapCoreSessionsToDomain,
  type RawCoreSession,
} from '@shared/sessions/mapping'
import { sumBillableDrivingKm } from '@shared/sessions/km'
import {
  aggregateArrivalLeg,
  aggregateSegmentsLeg,
  applyDepartureResolutions,
} from '@shared/sessions/travel-legs'
import type {
  GeoPoint,
  Session,
  SessionChecklist,
  SessionJourney,
  SessionLifecycleEvent,
} from '@shared/sessions/types'
import { assessRemoteArrival, canAddJourneyPing, withSessionCallId, computeJourneyStage, computeTravelEstimate, createEmptyChecklist, disciplineFromServiceType, emptyJourney, isPreShiftWizardComplete, isUndoCheckInWindowOpen, JOURNEY_ARRIVED_STAGE, resolveTravelDurationMs, shouldAutoArrive, UNDO_CHECK_IN_SERVER_GRACE_MS, undoCheckInRestoredState, undoWindowMsFor } from '@shared/sessions'
import { googleMapsDirectionsUrl } from '@shared/sessions/journey'
import { isFaceToFace } from '@shared/sessions/mapping'
import {
  mergeJourneyBackfill,
  validateJourneyBackfill,
  type JourneyBackfillFix,
} from '@shared/sessions/journey-backfill'
import { RAW_SESSIONS } from '@shared/sessions/fixtures'
import type { LifecycleEventResult, PreShiftCompleteResult, SessionsRepository } from './sessions-repository'
import { canCheckIn as canCheckInState, canCheckOut as canCheckOutState } from '../utils/format'

const STORAGE_KEY = 'ascend.sessions.mock.v1'

/** Narrogin, WA — used to give face-to-face fixtures a geocoded target. */
const NARROGIN: GeoPoint = { lat: -32.9365, lng: 117.1782 }

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

function isoAt(offsetMs: number): string {
  return new Date(Date.now() + offsetMs).toISOString()
}

/**
 * Sessions anchored to "now" so the Today / Upcoming lists always have content.
 * Shaped like the core app's inbound payload and run through the real mapper.
 */
function generateRelativeRawSessions(): RawCoreSession[] {
  const provider = { email: 'you@ascendhealthgroup.com.au', first_name: 'You', last_name: '' }
  return [
    {
      uuid: 'aaaaaaa1-0000-4000-8000-000000000001',
      provider,
      member: { email: 'maria@example.com', first_name: 'Maria', last_name: 'Gonzalez' },
      location: '12 Federation St, Narrogin, 6312, Western Australia',
      status: 'confirmed',
      // Starts soon — inside the check-in window for a live demo.
      start: isoAt(20 * 60 * 1000),
      end: isoAt(20 * 60 * 1000 + HOUR_MS),
      service_type: 'Support Work',
      face_to_face: true,
      non_face_to_face_session_type: null,
      notes: 'Bring updated care plan.',
      checkins: [],
    },
    {
      uuid: 'aaaaaaa1-0000-4000-8000-000000000002',
      provider,
      member: { email: 'james@example.com', first_name: 'James', last_name: 'Whitfield' },
      location: 'Telehealth',
      status: 'confirmed',
      start: isoAt(4 * HOUR_MS),
      end: isoAt(4 * HOUR_MS + 30 * 60 * 1000),
      service_type: 'Telehealth Consult',
      face_to_face: false,
      non_face_to_face_session_type: 'video',
      notes: '',
      checkins: [],
    },
    {
      uuid: 'aaaaaaa1-0000-4000-8000-000000000003',
      provider,
      member: { email: 'abdul@example.com', first_name: 'Abdul', last_name: 'Stehr' },
      location: '93168 Pfeffer Forges, Narrogin, 6312, Western Australia',
      status: 'confirmed',
      start: isoAt(DAY_MS + 2 * HOUR_MS),
      end: isoAt(DAY_MS + 3 * HOUR_MS),
      service_type: 'Physiotherapy',
      face_to_face: true,
      non_face_to_face_session_type: null,
      notes: '',
      session_uuid: '60b23b07-c234-4ebb-9435-d78f81467170',
      checkins: [],
    },
    {
      uuid: 'aaaaaaa1-0000-4000-8000-000000000004',
      provider,
      member: { email: 'priya@example.com', first_name: 'Priya', last_name: 'Anand' },
      location: '5 Clayton Rd, Narrogin, 6312, Western Australia',
      status: 'confirmed',
      start: isoAt(3 * DAY_MS + HOUR_MS),
      end: isoAt(3 * DAY_MS + 2 * HOUR_MS),
      service_type: 'Support Work',
      face_to_face: true,
      non_face_to_face_session_type: null,
      notes: '',
      checkins: [],
    },
    {
      uuid: 'aaaaaaa1-0000-4000-8000-000000000005',
      provider,
      member: { email: 'tom@example.com', first_name: 'Tom', last_name: 'Becker' },
      location: '21 Park Ave, Narrogin, 6312, Western Australia',
      status: 'completed',
      start: isoAt(-DAY_MS - 2 * HOUR_MS),
      end: isoAt(-DAY_MS - HOUR_MS),
      service_type: 'Support Work',
      face_to_face: true,
      non_face_to_face_session_type: null,
      notes: '',
      checkins: [],
    },
  ]
}

/** Give in-person sessions a geocoded target near the address (demo geofence). */
function withGeo(session: Session): Session {
  if (!isFaceToFace(session)) return session
  // Jitter each target slightly so geofence distances differ per session.
  const seed = session.uuid.charCodeAt(session.uuid.length - 1)
  return {
    ...session,
    geo: {
      lat: NARROGIN.lat + (seed % 7) * 0.0009,
      lng: NARROGIN.lng + (seed % 5) * 0.0011,
    },
  }
}

function seedSessions(): Session[] {
  const raw = [...generateRelativeRawSessions(), ...RAW_SESSIONS]
  return mapCoreSessionsToDomain(raw).map(withGeo)
}

function readStore(): Session[] | null {
  try {
    const json = localStorage.getItem(STORAGE_KEY)
    if (!json) return null
    const parsed = JSON.parse(json) as Session[]
    return Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}

function writeStore(sessions: Session[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions))
  } catch {
    /* storage is best-effort; in-memory copy still works for the session */
  }
}

/**
 * In-memory + localStorage-backed mock. Network-free, idempotent on
 * `uuid + type + clientEventId` like the real endpoint, and applies the same
 * per-event field updates so the demo matches production semantics.
 */
export class MockSessionsRepository implements SessionsRepository {
  private sessions: Session[]
  private appliedKeys = new Set<string>()

  constructor() {
    this.sessions = readStore() ?? seedSessions()
    writeStore(this.sessions)
  }

  /** Reset to freshly seeded data — handy for demos. */
  reset(): void {
    this.sessions = seedSessions()
    this.appliedKeys.clear()
    writeStore(this.sessions)
  }

  async listMySessions(): Promise<Session[]> {
    // Read-time departure resolution (assumed / linked-to-next-session) —
    // mirrors the list-my-sessions provider lens; never stored.
    return applyDepartureResolutions(this.sessions).map((s) => ({ ...s }))
  }

  async getSession(uuid: string): Promise<Session | null> {
    const found = applyDepartureResolutions(this.sessions).find((s) => s.uuid === uuid)
    return found ? { ...found } : null
  }

  async submitLifecycleEvent(event: SessionLifecycleEvent): Promise<LifecycleEventResult> {
    const index = this.sessions.findIndex((s) => s.uuid === event.uuid)
    if (index === -1) {
      throw new Error(`Session not found: ${event.uuid}`)
    }

    const key = `${event.uuid}#${event.type}#${event.clientEventId}`
    const current = this.sessions[index]
    const mockAhpSync =
      event.type === 'check-in' || event.type === 'check-out'
        ? ({ state: 'skipped', detail: 'Mock repository — no AHP outbound' } as const)
        : undefined
    if (this.appliedKeys.has(key)) {
      return {
        uuid: event.uuid,
        status: 'duplicate',
        lifecycleState: current.lifecycleState,
        kmTracked: current.kmTracked,
        eventType: event.type,
        ...(mockAhpSync ? { ahpSync: mockAhpSync } : {}),
      }
    }

    // Telehealth session call (mirrors the lambda): link the call; record a
    // start/end only when the session is in a state that allows it.
    const callTriggered =
      Boolean(event.callId) &&
      ((event.type === 'check-in' && event.startedBy === 'call') ||
        (event.type === 'check-out' && event.endedBy === 'call'))
    const callIds = callTriggered ? withSessionCallId(current.callIds, event.callId as string) : null
    if (
      callIds &&
      !(event.type === 'check-in'
        ? canCheckInState(current.lifecycleState)
        : canCheckOutState(current.lifecycleState))
    ) {
      this.sessions[index] = { ...current, callIds }
      this.appliedKeys.add(key)
      writeStore(this.sessions)
      return {
        uuid: event.uuid,
        status: 'linked',
        lifecycleState: current.lifecycleState,
        kmTracked: current.kmTracked,
        eventType: event.type,
        patch: { callIds },
      }
    }

    const segments = Array.isArray(event.driveSegments) ? event.driveSegments : []
    const billableKm =
      segments.length > 0 ? sumBillableDrivingKm(segments) : event.kmTracked ?? null

    const next: Session = { ...current, ...(callIds ? { callIds } : {}) }
    switch (event.type) {
      case 'check-in':
        next.checkIn = event.location ?? null
        next.actualStart = event.location?.timestamp ?? event.occurredAt
        next.lifecycleState = 'checked-in'
        // Audit the trigger (mirrors the lambda): dwell auto-record vs tap.
        next.startedBy =
          event.startedBy === 'auto-gps' ? 'auto-gps' : callTriggered ? 'call' : 'provider'
        // Remote-arrival flag (mirrors the lambda): manual arrival > 1 km away.
        {
          const remote =
            next.startedBy === 'provider'
              ? assessRemoteArrival(event.location ?? null, current.geo ?? null)
              : null
          next.arrivalLocationFlag = remote?.remote
            ? { distanceKm: remote.distanceKm, at: next.actualStart ?? event.occurredAt }
            : null
        }
        // Check-in implies arrival (mirrors the lambda): close an en_route
        // journey so the flow never shows "travelling" during the session —
        // this is the GPS-fallback path when auto-arrival never fired.
        if (current.journey && current.journey.status === 'en_route') {
          next.journey = {
            ...current.journey,
            status: 'arrived',
            arrivedAt: current.journey.arrivedAt ?? next.actualStart,
            stage: JOURNEY_ARRIVED_STAGE,
          }
        }
        break
      case 'km-update':
        next.kmTracked = billableKm
        next.driveSegments = segments
        if (current.lifecycleState === 'checked-in') next.lifecycleState = 'tracking'
        break
      case 'check-out':
        next.checkOut = event.location ?? null
        next.actualEnd = event.location?.timestamp ?? event.occurredAt
        next.kmTracked = billableKm
        next.driveSegments = segments
        next.lifecycleState = 'checked-out'
        if (callTriggered) next.endedBy = 'call'
        // Per-leg travel breakdown — mirrors update-session-state.
        next.travel = {
          ...(current.travel ?? {}),
          // Fresh evidence only — never clobber a stored leg (mirrors lambda).
          arrival:
            aggregateArrivalLeg(current.journey ?? null, {
              checkInAt: current.actualStart ?? null,
            }) ??
            current.travel?.arrival ??
            null,
          during: aggregateSegmentsLeg(segments),
        }
        break
      // NOTE: no `departure-end` case — the web app no longer tracks a
      // departure leg (owner rule 2026-10-07 revision: assumed/linked at read
      // time). Only the live lambda still accepts the event, from phone
      // builds ≤ Android 119 / iOS 81.
      case 'undo-check-in': {
        // Mirrors update-session-state: window-validated rollback to the
        // pre-record state; journey ('arrived') left intact. The window is
        // longer for an auto-record (60 s) than a manual tap (15 s).
        const windowMs =
          undoWindowMsFor(current.startedBy) + UNDO_CHECK_IN_SERVER_GRACE_MS
        if (!isUndoCheckInWindowOpen(current.actualStart ?? null, Date.now(), windowMs)) {
          throw new Error('UNDO_WINDOW_EXPIRED: the undo window for this arrival record has passed')
        }
        next.checkInUndone = {
          undoneAt: event.occurredAt,
          previousActualStart: current.actualStart ?? null,
          previousStartedBy: current.startedBy ?? null,
        }
        next.checkIn = null
        next.actualStart = null
        next.startedBy = null
        next.lifecycleState = undoCheckInRestoredState(current)
        break
      }
      case 'cancel':
        // Mirrors the backend: terminal side exit, journey closed, km retained.
        next.lifecycleState = 'cancelled'
        next.cancelledAt = event.occurredAt
        if (segments.length > 0) {
          next.kmTracked = billableKm
          next.driveSegments = segments
        } else if (event.kmTracked != null) {
          next.kmTracked = event.kmTracked
        }
        if (next.journey && (next.journey.status === 'en_route' || next.journey.status === 'arrived')) {
          next.journey = { ...next.journey, status: 'ended' }
        }
        break
    }

    this.sessions[index] = next
    this.appliedKeys.add(key)
    writeStore(this.sessions)

    // Mirror the lambda's echo of the written fields (see LifecycleEventResult.patch).
    const patch: Partial<Session> = {}
    for (const field of [
      'actualStart',
      'actualEnd',
      'checkIn',
      'checkOut',
      'kmTracked',
      'driveSegments',
      'travel',
      'journey',
      'startedBy',
      'endedBy',
      'arrivalLocationFlag',
      'cancelledAt',
      'callIds',
    ] as const) {
      if (next[field] !== current[field]) {
        ;(patch as Record<string, unknown>)[field] = next[field] ?? null
      }
    }

    return {
      uuid: event.uuid,
      status: 'applied',
      lifecycleState: next.lifecycleState,
      kmTracked: next.kmTracked,
      eventType: event.type,
      patch,
      ...(mockAhpSync ? { ahpSync: mockAhpSync } : {}),
    }
  }

  async getSessionJourney(uuid: string) {
    const session = await this.getSession(uuid)
    if (!session) throw new Error(`Session not found: ${uuid}`)
    return {
      uuid,
      journey: session.journey ?? emptyJourney(),
      leaveByAt: session.leaveByAt ?? null,
      preShiftChecklist: session.preShiftChecklist ?? null,
      mapsUrl: session.geo ? googleMapsDirectionsUrl(session.geo) : null,
      lifecycleState: session.lifecycleState,
      cancellationRequested: session.cancellationRequested ?? null,
    }
  }

  async savePreShiftChecklist(
    uuid: string,
    body: {
      responses: Record<string, boolean>
      completed?: boolean
      providerLocation?: { lat: number; lng: number }
    },
  ): Promise<PreShiftCompleteResult> {
    const index = this.sessions.findIndex((s) => s.uuid === uuid)
    if (index === -1) throw new Error(`Session not found: ${uuid}`)
    const session = this.sessions[index]
    const discipline = disciplineFromServiceType(session.serviceType)
    const existing = session.preShiftChecklist ?? createEmptyChecklist(discipline, 'pre_shift')
    const responses = { ...existing.responses, ...body.responses }
    const completed =
      body.completed === true || (body.completed !== false && isPreShiftWizardComplete(discipline, responses))
    const checklist: SessionChecklist = {
      ...existing,
      responses,
      completedAt: completed ? Date.now() : existing.completedAt,
    }

    let leaveByAt = session.leaveByAt ?? null
    let travelDurationMs: number | null = null
    if (completed && body.providerLocation && session.geo) {
      const estimate = computeTravelEstimate(body.providerLocation, session.geo, session.start)
      // Same sanity clamp as the backend: never persist an absurd estimate.
      if (!estimate.unreliable) {
        leaveByAt = estimate.leaveByAt
        travelDurationMs = estimate.travelDurationMs
      }
    }

    this.sessions[index] = { ...session, preShiftChecklist: checklist, leaveByAt, travelDurationMs }
    writeStore(this.sessions)
    return { checklist, leaveByAt, travelDurationMs }
  }

  async startJourney(
    uuid: string,
    ping: import('./sessions-repository').JourneyPingInput,
  ): Promise<{ journey: SessionJourney; leaveByAt: number | null }> {
    const index = this.sessions.findIndex((s) => s.uuid === uuid)
    if (index === -1) throw new Error(`Session not found: ${uuid}`)
    const session = this.sessions[index]
    const journey: SessionJourney = {
      status: 'en_route',
      pings: [{ ...ping, timestamp: ping.timestamp ?? Date.now(), stage: ping.stage ?? 2 }],
      startedAt: Date.now(),
      arrivedAt: null,
      stage: ping.stage ?? 2,
    }
    let leaveByAt = session.leaveByAt ?? null
    if (session.geo != null) {
      const estimate = computeTravelEstimate(ping, session.geo, session.start)
      if (!estimate.unreliable) leaveByAt = estimate.leaveByAt
    }
    this.sessions[index] = { ...session, journey, leaveByAt }
    writeStore(this.sessions)
    return { journey, leaveByAt }
  }

  async pingJourney(
    uuid: string,
    ping: import('./sessions-repository').JourneyPingInput,
  ): Promise<SessionJourney> {
    const index = this.sessions.findIndex((s) => s.uuid === uuid)
    if (index === -1) throw new Error(`Session not found: ${uuid}`)
    const session = this.sessions[index]
    const current = session.journey ?? emptyJourney()
    if (!canAddJourneyPing(current)) throw new Error('Maximum journey pings reached')
    const travelDurationMs = resolveTravelDurationMs({
      start: session.start,
      leaveByAt: session.leaveByAt,
      travelDurationMs: session.travelDurationMs,
    })
    const pings = [...current.pings, { ...ping, timestamp: ping.timestamp ?? Date.now() }]

    // GPS auto-arrival (mirrors the session-journey lambda): an accuracy-
    // checked real fix inside the tight arrival radius flips the journey to
    // `arrived` — the "I've arrived" button no longer exists.
    if (
      shouldAutoArrive({
        journey: current,
        destination: session.geo ?? null,
        ping: {
          lat: ping.lat,
          lng: ping.lng,
          accuracy: ping.accuracy,
          locationSource: ping.locationSource ?? 'gps',
          isMock: ping.isMock,
        },
      })
    ) {
      const journey: SessionJourney = {
        ...current,
        status: 'arrived',
        pings,
        arrivedAt: Date.now(),
        stage: JOURNEY_ARRIVED_STAGE,
      }
      this.sessions[index] = { ...session, journey }
      writeStore(this.sessions)
      return journey
    }

    const nextStage = computeJourneyStage({
      journey: { ...current, pings },
      destination: session.geo ?? null,
      currentPosition: ping,
      travelDurationMs,
    })
    const journey: SessionJourney = { ...current, pings, stage: nextStage }
    this.sessions[index] = { ...session, journey }
    writeStore(this.sessions)
    return journey
  }

  async backfillJourney(uuid: string, fixes: JourneyBackfillFix[]): Promise<SessionJourney> {
    const index = this.sessions.findIndex((s) => s.uuid === uuid)
    if (index === -1) throw new Error(`Session not found: ${uuid}`)
    const session = this.sessions[index]
    const current = session.journey ?? emptyJourney()
    if (current.status !== 'en_route' && current.status !== 'arrived') {
      throw new Error('Journey must be en_route or arrived to accept backfill')
    }
    // Mirrors the session-journey lambda: validate against the journey
    // window, merge by fix timestamp, backdate arrival to the qualifying fix.
    const { accepted } = validateJourneyBackfill({ journey: current, backfill: fixes })
    const merged = mergeJourneyBackfill({
      journey: current,
      accepted,
      destination: session.geo ?? null,
    })
    this.sessions[index] = { ...session, journey: merged.journey }
    writeStore(this.sessions)
    return merged.journey
  }

  async arriveJourney(
    uuid: string,
    ping?: Pick<import('./sessions-repository').JourneyPingInput, 'lat' | 'lng' | 'timestamp' | 'accuracy'>,
  ): Promise<SessionJourney> {
    const index = this.sessions.findIndex((s) => s.uuid === uuid)
    if (index === -1) throw new Error(`Session not found: ${uuid}`)
    const session = this.sessions[index]
    const current = session.journey ?? emptyJourney()
    if (current.status === 'arrived') return current
    if (current.status !== 'en_route') {
      throw new Error('Arrival can only be declared while travelling')
    }
    const pings =
      ping && current.pings.length < 5
        ? [...current.pings, { ...ping, timestamp: ping.timestamp ?? Date.now(), stage: JOURNEY_ARRIVED_STAGE }]
        : current.pings
    const journey: SessionJourney = {
      ...current,
      status: 'arrived',
      pings,
      arrivedAt: Date.now(),
      stage: JOURNEY_ARRIVED_STAGE,
    }
    this.sessions[index] = { ...session, journey }
    writeStore(this.sessions)
    return journey
  }

  async requestCancellation(
    uuid: string,
    input: { reason: import('@shared/sessions/types').CancellationReason; note?: string },
  ) {
    const index = this.sessions.findIndex((s) => s.uuid === uuid)
    if (index === -1) throw new Error(`Session not found: ${uuid}`)
    const session = this.sessions[index]
    if (session.cancellationRequested) {
      return { status: 'duplicate' as const, cancellationRequested: session.cancellationRequested }
    }
    const cancellationRequested = {
      requestedAt: Date.now(),
      requestedByEmail: session.memberEmail,
      requestedByName: session.memberName,
      reason: input.reason,
      ...(input.note ? { note: input.note } : {}),
    }
    this.sessions[index] = { ...session, cancellationRequested }
    writeStore(this.sessions)
    return { status: 'applied' as const, cancellationRequested }
  }
}
